import { useAuth } from "AuthProvider";
import { Formik, Form as FormikForm } from "formik";
import useFetch from "hooks/useFetch";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  FormikTextarea,
  FormikTextField,
  Icon,
  IconButton,
  PageHeader,
  Select,
  Skeleton,
  Toggle,
  styles as ui,
  useConfirm,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import DateField from "../EventPage/DateField";
import styles from "./InformationPage.module.scss";

interface InformationForm {
  title: string;
  description: string;
  selectedColor: string;
  activated: boolean;
  activatedForTv: boolean;
  priority?: number;
}

interface Information {
  id: number;
  tittel: string;
  tekst: string;
  color: string;
  date: string;
  activated: boolean;
  image?: string;
  mosqueOrgNr: string;
  activatedForTv: boolean;
  priority?: number;
}

const MAX_DESCRIPTION_LENGTH = 255;

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Overskrift er påkrevd"),
  description: Yup.string()
    .required("Beskrivelse er påkrevd")
    .max(MAX_DESCRIPTION_LENGTH, `Maks ${MAX_DESCRIPTION_LENGTH} tegn tillatt`),
  selectedColor: Yup.string(),
});

const colorOptions = [
  { name: "green", hex: "#E1F5B7", label: "Grønn" },
  { name: "blue", hex: "#BDD7F8", label: "Blå" },
  { name: "orange", hex: "#FFD4BC", label: "Oransje" },
  { name: "purple", hex: "#BEBEE9", label: "Lilla" },
  { name: "white", hex: "#FFFFFF", label: "Hvit" },
];

const PRIORITIES = Array.from({ length: 100 }, (_, index) => index + 1);

/** Options 1–100, plus the current value if it is outside that range. */
const priorityOptions = (current?: number | null) =>
  current !== undefined && current !== null && !PRIORITIES.includes(current)
    ? [current, ...PRIORITIES]
    : PRIORITIES;

const InformationPage: React.FC = () => {
  const { orgNumber, selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: infoData,
    isLoading: isInfoLoading,
    execute: fetchInformation,
  } = useFetch<Information[]>();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [information, setInformation] = useState<Information[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingInformation, setEditingInformation] =
    useState<Information | null>(null);

  const fetchData = async () => {
    const response = await fetchInformation(
      `/api/v1/information/org/${selectedOrgNumber}`,
      "GET",
    );
    if (response) {
      setInformation(response);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedOrgNumber]);

  const handleSubmit = async (values: InformationForm, { resetForm }: any) => {
    if (!selectedDate) {
      setDateError("Dato er påkrevd");
      return;
    } else {
      setDateError(null);
    }

    const formattedDate = `${selectedDate.getFullYear()}-${String(
      selectedDate.getMonth() + 1,
    ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;

    const informationRequestDto = {
      date: formattedDate,
      tittel: values.title,
      tekst: values.description,
      mosqueOrgNr: selectedOrgNumber,
      activated: values.activated,
      color: values.selectedColor,
      image: "",
      activatedForTv: values.activatedForTv,
      priority: values.priority,
    };

    try {
      const response = await execute(
        "/api/v1/information",
        "POST",
        informationRequestDto,
      );

      resetForm();
      setSelectedDate(null);
      fetchData();
      if (response) {
        toast.success("Informasjonen er lagret.");
      } else {
        toast.error("Kunne ikke lagre informasjonen. Prøv igjen.");
      }
    } catch (error: any) {
      console.error("Error:", error.response || error.message || error);
    }
  };

  const handleSaveEdit = (values: InformationForm) => {
    if (!editingInformation) return;

    const currentTime = new Date();

    const formattedDate = `${currentTime.getFullYear()}-${String(
      currentTime.getMonth() + 1,
    ).padStart(2, "0")}-${String(currentTime.getDate()).padStart(2, "0")}`;

    const payload = {
      tittel: values.title,
      tekst: values.description,
      activated: values.activated,
      mosqueOrgNr: selectedOrgNumber,
      date: formattedDate,
      color: values.selectedColor,
      activatedForTv: values.activatedForTv,
      priority: values.priority,
    };

    try {
      execute(
        `/api/v1/information/${selectedOrgNumber}/${editingInformation.id}`,
        "PATCH",
        payload,
      );
      setInformation((prevInformation) =>
        prevInformation.map((info) =>
          info.id === editingInformation.id
            ? {
                ...info,
                tittel: values.title,
                tekst: values.description,
                activated: values.activated,
                activatedForTv: values.activatedForTv,
                priority: values.priority || 0,
                color: values.selectedColor,
              }
            : info,
        ),
      );
      setShowModal(false);
      setEditingInformation(null);
      toast.success("Endringene er lagret.");
    } catch (error: any) {
      console.error("Failed to update information", error);
    }
  };

  const deleteInformation = async (id: number) => {
    try {
      await execute(`/api/v1/information/${id}`, "DELETE");
      setInformation((prev) => prev.filter((info) => info.id !== id));
      toast.success("Informasjonen er slettet.");
    } catch (error: any) {
      console.error("Failed to delete information", error);
    }
  };

  const confirmDeleteInformation = async (info: Information) => {
    const ok = await confirm({
      title: "Slett informasjon?",
      message: (
        <span>
          Innlegget <b>«{info.tittel}»</b> fjernes fra appen og TV-skjermen.
          Dette kan ikke angres.
        </span>
      ),
      confirmLabel: "Slett",
      tone: "danger",
    });
    if (ok) await deleteInformation(info.id);
  };

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowModal(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showModal]);

  const formatDate = (date: string) => {
    const parsed = new Date(date);
    return isNaN(parsed.getTime())
      ? date
      : parsed.toLocaleDateString("nb-NO", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
  };

  /** Fields shared by the create form and the edit dialog. */
  const renderFields = (
    values: InformationForm,
    setFieldValue: (field: string, value: any) => void,
    dateField: React.ReactNode,
  ) => (
    <>
      <FormikTextField
        name="title"
        label="Overskrift"
        required
        placeholder="Skriv overskrift …"
      />
      <FormikTextarea
        name="description"
        label="Beskrivelse"
        required
        rows={3}
        placeholder="Skriv beskrivelse …"
        maxLength={MAX_DESCRIPTION_LENGTH}
        maxChars={MAX_DESCRIPTION_LENGTH}
      />
      {dateField}
      <div className={ui.fld}>
        <span className={ui.lbl}>
          Farge<span className={ui.req}> *</span>
        </span>
        <div className={styles.colors} role="radiogroup" aria-label="Farge">
          {colorOptions.map((colorOption) => {
            const on = values.selectedColor === colorOption.hex;
            return (
              <button
                key={colorOption.hex}
                type="button"
                role="radio"
                aria-checked={on}
                className={styles.colorOption}
                onClick={() => setFieldValue("selectedColor", colorOption.hex)}
              >
                <span
                  className={`${styles.swatch} ${on ? styles.swatchOn : ""}`}
                  style={{ backgroundColor: colorOption.hex }}
                />
                <span className={styles.help}>{colorOption.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <Select
        label="Prioritet"
        required
        value={values.priority ?? ""}
        onChange={(e) =>
          setFieldValue("priority", parseInt(e.target.value || "50", 10))
        }
        help="Jo lavere tall, jo høyere opp. 1–100."
      >
        {values.priority === undefined || values.priority === null ? (
          <option value="" disabled>
            Velg
          </option>
        ) : null}
        {priorityOptions(values.priority).map((num) => (
          <option key={num} value={num}>
            {num}
          </option>
        ))}
      </Select>
      <Toggle
        label={<span className={styles.toggleLabel}>Vis i appen</span>}
        name="activated"
        checked={values.activated}
        onChange={(e) => setFieldValue("activated", e.target.checked)}
      />
      <Toggle
        label={<span className={styles.toggleLabel}>Vis på TV-skjermen</span>}
        name="activatedForTv"
        checked={values.activatedForTv}
        onChange={(e) => setFieldValue("activatedForTv", e.target.checked)}
      />
    </>
  );

  const openEditModal = (info: Information) => {
    setEditingInformation(info);
    setShowModal(true);
  };

  return (
    <>
      <PageHeader
        title="Informasjon"
        subtitle="Korte meldinger som vises øverst i appen og på TV-skjermen."
      />

      <div className={styles.layout}>
        <Card title="Ny informasjon">
          <Formik
            initialValues={{
              title: "",
              description: "",
              selectedColor: "",
              activated: true,
              activatedForTv: false,
              priority: 0,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isValid, dirty, setFieldValue, values }) => (
              <FormikForm className={styles.form} noValidate>
                {renderFields(
                  values,
                  setFieldValue,
                  <div className={ui.fld}>
                    <DateField
                      label="Dato"
                      required
                      selected={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                    />
                    {dateError && (
                      <span className={ui.errt} role="alert">
                        {dateError}
                      </span>
                    )}
                  </div>,
                )}
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={!(isValid && dirty && selectedDate)}
                  icon={<Icon name="check" size={16} strokeWidth={2} />}
                >
                  {isLoading ? "Lagrer …" : "Lagre informasjon"}
                </Button>
                {error && (
                  <Alert tone="error">Noe gikk galt. Prøv igjen senere.</Alert>
                )}
              </FormikForm>
            )}
          </Formik>
        </Card>

        <Card title="Alle informasjonsinnlegg">
          {isInfoLoading ? (
            <div className={styles.list}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={110} radius={20} />
              ))}
            </div>
          ) : error ? (
            <Alert tone="error">Kunne ikke hente informasjonen.</Alert>
          ) : information.length > 0 ? (
            <div className={styles.list}>
              {information.map((info) => (
                <article
                  key={info.id}
                  className={styles.item}
                  style={{ borderLeftColor: info.color || undefined }}
                >
                  <div className={styles.itemTitleRow}>
                    <b className={styles.itemTitle}>{info.tittel}</b>
                    <IconButton
                      label="Rediger"
                      icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                      onClick={() => openEditModal(info)}
                    />
                    <IconButton
                      label="Slett"
                      icon={<Icon name="trash" size={17} strokeWidth={1.9} />}
                      onClick={() => confirmDeleteInformation(info)}
                    />
                  </div>
                  <span className={styles.itemText}>{info.tekst}</span>
                  <div className={styles.badges}>
                    {info.activated ? (
                      <Badge tone="mint">App</Badge>
                    ) : (
                      <Badge tone="grey">Skjult i app</Badge>
                    )}
                    {info.activatedForTv ? (
                      <Badge tone="blue">TV</Badge>
                    ) : (
                      <Badge tone="grey">Ikke på TV</Badge>
                    )}
                    {info.priority !== undefined && info.priority !== null && (
                      <Badge tone="gold">Prioritet {info.priority}</Badge>
                    )}
                    <span className={styles.itemDate}>
                      <Icon name="cal" size={14} strokeWidth={2} />
                      {formatDate(info.date)}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState icon="info" title="Ingen informasjon">
              Informasjon du lagrer vises her.
            </EmptyState>
          )}
        </Card>
      </div>

      {showModal && editingInformation && (
        <div
          className={ui.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div
            className={ui.modal}
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-info-title"
          >
            <Formik
              initialValues={{
                title: editingInformation.tittel,
                description: editingInformation.tekst,
                activated: editingInformation.activated,
                selectedColor: editingInformation.color,
                activatedForTv: editingInformation.activatedForTv,
                priority: editingInformation.priority,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSaveEdit}
            >
              {({ isValid, values, setFieldValue }) => (
                <FormikForm noValidate>
                  <h2 className={ui.mh} id="edit-info-title">
                    <span className={ui.mhIcon} aria-hidden="true">
                      <Icon name="edit" size={18} />
                    </span>
                    Rediger informasjon
                    <IconButton
                      className={styles.modalClose}
                      label="Lukk"
                      icon={<Icon name="x" size={18} strokeWidth={2} />}
                      onClick={() => setShowModal(false)}
                    />
                  </h2>
                  <div className={ui.mb}>
                    {renderFields(values, setFieldValue, null)}
                  </div>
                  <div className={ui.mf}>
                    <Button
                      variant="secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Avbryt
                    </Button>
                    <Button type="submit" disabled={!isValid}>
                      Lagre endringer
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </div>
        </div>
      )}
    </>
  );
};

export default InformationPage;
